import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InformedRadioGroup,
  InformedTextField,
  radioGroupTypes
} from '@nw/design-system/omega';
import { RightArrow } from '@nw/shared/ui';
import {
  convertNumberToLocaleCurrency,
  LOADING,
  SUCCESS
} from '@nw/shared/utils';

import {
  YET_TO_BE_DECIDED_UNIVERSITY_LABEL,
  YET_TO_BE_DECIDED_UNIVERSITY_VALUE
} from '../../constants/ApplicationFeeConstants';
import { useNiatApplicationMachineContext } from '../../context/NiatApplicationMachineProvider';
import {
  CreateUserProgramInstituteEvent,
  UpdateFieldValues
} from '../../types/niatApplicationMachineTypes';
import { OnClickContinueToPayEvent } from '../../types/productMachineTypes';
import { getInstituteDisplayLabel } from '../../utils/EnrollmentFeeUtils';
import { SeatReservationFeeSectionValidations } from '../../utils/ValidationUtils/SeatReservationFeeSectionValidations';

import { FieldContainer } from '../ApplicationSectionsPage/styledComponents';
import InstituteAndAccomodation from '../ProductFeeSectionCommonComponents/InstituteAndAccomodation';
import ProductSectionHeaderComponent from '../ProductFeeSectionCommonComponents/ProductSectionHeaderComponent';
import RenderCouponCodeFieldAndApplyButton from '../ProductFeeSectionCommonComponents/RenderCouponCodeFieldAndApplyButton';
import { TermsAndConditions } from '../ProductFeeSectionCommonComponents/TermsAndConditions';
import { UserDetailsAndInfoCard } from '../ProductFeeSectionCommonComponents/UserDetailsAndInfoCard';

import { usePaymentGatewayHandler } from './PaymentHandler';
import {
  ApplicationFeeCardContainer,
  ApplicationFeeFieldsContainer,
  ApplicationFeeLabel,
  ApplicationFeeLabelContainer,
  ApplicationFeeSectionContainer,
  ApplicationFeeValue,
  ApplicationFeeValueAfterDiscount,
  ContinueToPayButton,
  SectionFooterContainer,
  SectionFooterContainerGlobalStyle,
  TakeAdmissionCompactField
} from './styledComponent';

interface SeatReservationFeeProps {
  sectionName: string;
  onSuccessPayment: () => void;
}
const SeatReservationFee = ({
  sectionName,
  onSuccessPayment
}: SeatReservationFeeProps) => {
  const { paymentModal } = usePaymentGatewayHandler({
    sectionName,
    onSuccessPayment
  });

  const { t } = useTranslation();
  const { niatApplicationMachineState, niatApplicationMachineSend } =
    useNiatApplicationMachineContext();
  const {
    context: {
      initialValues,
      availableProgramInstitutesData,
      userGraphQlProfileDetails,
      productsSectionMachineRefDetails: {
        [sectionName]: {
          state: {
            context: {
              productDetail,
              inviteCode,
              apiStatus: {
                getCouponCodeDetailApiStatus,
                submitProductOrCreatePaymentApiStatus,
                getSelectedProductPaymentServiceApiStatus
              }
            }
          },
          send: productMachineSend
        }
      }
    }
  } = niatApplicationMachineState;

  const inviteCodeField = 'inviteCode';

  const formattedApplicationFee = useMemo(
    () => convertNumberToLocaleCurrency(productDetail?.productFee ?? 0),
    [productDetail?.productFee]
  );

  const formattedApplicationFeeAfterDiscount = useMemo(
    () =>
      convertNumberToLocaleCurrency(
        productDetail?.productFeeAfterDiscount ?? 0
      ),
    [productDetail?.productFeeAfterDiscount]
  );
  const onClickContinueToPay = (values, formProps) => {
    const { formApi } = formProps;
    formApi.validate();
    const validatedFormState: any = formApi.getState();

    const correctedEmailValue = String(values['correctEmail'] ?? '');
    if (validatedFormState.invalid) {
      return;
    }

    const preferredUniversityId = String(values['preferredUniversity'] ?? '');
    const preferredUniversityName =
      preferredUniversityId === YET_TO_BE_DECIDED_UNIVERSITY_VALUE
        ? YET_TO_BE_DECIDED_UNIVERSITY_LABEL
        : getInstituteDisplayLabel(
            preferredUniversityId,
            availableProgramInstitutesData
          );

    const payloadValues = {
      ...initialValues,
      ...values,
      preferredUniversity: preferredUniversityName,
      EMAIL_ADDRESS:
        correctedEmailValue ||
        userGraphQlProfileDetails?.email ||
        initialValues['EMAIL_ADDRESS'] ||
        ''
    };
    const existingEmail =
      userGraphQlProfileDetails?.email || initialValues['EMAIL_ADDRESS'] || '';
    if (
      values['isEmailCorrect'] === 'NO' &&
      correctedEmailValue &&
      correctedEmailValue !== existingEmail
    ) {
      niatApplicationMachineSend({
        type: 'UPDATE_FIELD_VALUES',
        fieldDetail: {
          field: 'EMAIL_ADDRESS',
          value: correctedEmailValue,
          extraDetailToUpdate: {}
        },
        values: {
          EMAIL_ADDRESS: correctedEmailValue
        }
      } as UpdateFieldValues);
    }
    if (preferredUniversityId !== YET_TO_BE_DECIDED_UNIVERSITY_VALUE) {
      niatApplicationMachineSend({
        type: 'CREATE_USER_PROGRAM_INSTITUTE',
        values: {
          preferredUniversity: preferredUniversityId,
          accommodationMode: payloadValues.accommodationMode
        }
      } as CreateUserProgramInstituteEvent);
    }
    productMachineSend({
      type: 'ON_CLICK_CONTINUE_TO_PAY',
      values: payloadValues,
      userProfileDetails: userGraphQlProfileDetails
    } as OnClickContinueToPayEvent);
  };

  const isContinueToPayButtonLoading = () =>
    getSelectedProductPaymentServiceApiStatus === LOADING ||
    submitProductOrCreatePaymentApiStatus === LOADING;
  return (
    <ApplicationFeeSectionContainer className="application-fee-section">
      <SectionFooterContainerGlobalStyle />
      <ProductSectionHeaderComponent
        title="Reserve Seat at NIAT"
        titleDescription="Complete your Seat Reservation fee and confirm your admission slot."
      />
      <UserDetailsAndInfoCard sectionName={sectionName} />
      <ApplicationFeeCardContainer
        allowEmptyStrings={true}
        initialValues={{
          inviteCode: inviteCode ?? '',
          communicationConsent: false,
          nxtwaveTermsAndConditions: false,
          nxtwaveRefundPolicy: true
        }}
      >
        {
          ((formProps) => {
            const { formState } = formProps;
            const { values, errors, touched } = formState as any;
            const hasEffectiveDiscount =
              productDetail?.productFeeAfterDiscount != null &&
              productDetail?.productFee != null &&
              productDetail.productFeeAfterDiscount < productDetail.productFee;
            const applicationFeeCouponAppliedStatus =
              getCouponCodeDetailApiStatus === SUCCESS && hasEffectiveDiscount;
            const applicationFee = () => {
              if (applicationFeeCouponAppliedStatus) {
                return (
                  <>
                    <span>{formattedApplicationFeeAfterDiscount}</span>
                    <ApplicationFeeValueAfterDiscount>
                      {formattedApplicationFee}
                    </ApplicationFeeValueAfterDiscount>
                  </>
                );
              }
              return (
                <>
                  <span>{formattedApplicationFee}</span>
                </>
              );
            };
            return (
              <>
                <ApplicationFeeFieldsContainer>
                  <ApplicationFeeLabelContainer>
                    <ApplicationFeeLabel>
                      Seat Reservation Fee
                    </ApplicationFeeLabel>
                    <ApplicationFeeValue>
                      {applicationFee()}
                    </ApplicationFeeValue>
                  </ApplicationFeeLabelContainer>
                </ApplicationFeeFieldsContainer>
                <FieldContainer>
                  <InformedRadioGroup
                    field="isEmailCorrect"
                    label={`Is ${userGraphQlProfileDetails?.email} the correct email address for important updates?`}
                    key="isEmailCorrect"
                    values={values}
                    error={errors['isEmailCorrect']}
                    touched={touched?.isEmailCorrect}
                    isDisabled={false}
                    radioOptions={[
                      { label: 'Yes', value: 'YES' },
                      { label: 'No', value: 'NO' }
                    ]}
                    validateOnBlur={false}
                    validateOnChange={false}
                    type={radioGroupTypes.simple as any}
                    isCustomizedRadioOptions={false}
                    validationSchemaObject={SeatReservationFeeSectionValidations(
                      {
                        t
                      }
                    )}
                  />
                </FieldContainer>
                {values?.isEmailCorrect === 'NO' && (
                  <TakeAdmissionCompactField>
                    <FieldContainer>
                      <InformedTextField
                        field="correctEmail"
                        label="Enter your correct E-mail"
                        placeholder="Enter E-mail"
                        fieldType="text"
                        validateOnBlur={true}
                        validateOnChange={true}
                        validationSchemaObject={SeatReservationFeeSectionValidations(
                          { t }
                        )}
                        touched={touched?.correctEmail}
                        error={errors['correctEmail']}
                        value={values?.correctEmail}
                        shouldShowIcon={false}
                        isDisabled={false}
                        isMarginBottomZero
                      />
                    </FieldContainer>
                  </TakeAdmissionCompactField>
                )}
                <TakeAdmissionCompactField>
                  <FieldContainer>
                    <RenderCouponCodeFieldAndApplyButton
                      field="inviteCode"
                      labelText="Invite Code"
                      sectionName={sectionName}
                      HideRightElement={true}
                      shouldShowDiscountMessage={false}
                      shouldShowCongratulationsModal={false}
                      shouldShowClearButton={false}
                      formProps={formProps}
                    />
                  </FieldContainer>
                </TakeAdmissionCompactField>
                <InstituteAndAccomodation
                  formProps={formProps}
                  validationSchemaObject={SeatReservationFeeSectionValidations({
                    t
                  })}
                  includeYetToDecide={false}
                />
                {TermsAndConditions(formProps as any)}
                <SectionFooterContainer>
                  <ContinueToPayButton
                    variant="Primary"
                    size="Large"
                    onClick={() => onClickContinueToPay(values, formProps)}
                    isLoading={isContinueToPayButtonLoading()}
                    className="application-fee-continue-to-pay-button"
                    isDisabled={Boolean(errors[inviteCodeField])}
                    shouldShrinkButtonWhileLoading={false}
                  >
                    Confirm and Pay
                    <RightArrow fill={'#fff'} />
                  </ContinueToPayButton>
                </SectionFooterContainer>
              </>
            );
          }) as any
        }
      </ApplicationFeeCardContainer>
      {paymentModal()}
    </ApplicationFeeSectionContainer>
  );
};

export default SeatReservationFee;
